import {
  createContext,
  useContext,
  useReducer,
  useMemo,
  type ReactNode,
} from 'react';
import { useLiff } from './LiffContext';
import type {
  CustomerProfile,
  CheckStatusResponse,
  SignUpRequest,
  SignInResponse,
  UpdateProfileRequest,
} from '../types/customer';
import { authReducer, initialAuthState } from './authReducer';
import { useAuthCallbacks } from './useAuthCallbacks';

interface AuthContextType {
  accessToken: string | null;
  customer: CustomerProfile | null;
  customerStatus: CheckStatusResponse | null;
  isAuthenticated: boolean;
  isAuthLoading: boolean;
  isInitialized: boolean;
  authError: string | null;
  checkUserStatus: () => Promise<CheckStatusResponse | null>;
  signIn: () => Promise<SignInResponse | null>;
  signUp: (data: Omit<SignUpRequest, 'id_token'>) => Promise<{ pending: boolean } | null>;
  signOut: () => Promise<void>;
  refreshAuth: () => Promise<SignInResponse | null>;
  fetchProfile: () => Promise<CustomerProfile | null>;
  updateProfile: (data: UpdateProfileRequest) => Promise<void>;
  clearAuthError: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { liff, isLoggedIn, isReady, idToken: liffIdToken } = useLiff();
  const [authState, dispatch] = useReducer(authReducer, initialAuthState);

  const { accessToken, customer, customerStatus, isAuthLoading, isInitialized, authError } = authState;

  const callbacks = useAuthCallbacks({
    liff,
    isLoggedIn,
    isReady,
    liffIdToken,
    accessToken,
    dispatch,
  });

  const contextValue = useMemo(
    () => ({
      accessToken,
      customer,
      customerStatus,
      isAuthenticated: !!accessToken && !!customer,
      isAuthLoading,
      isInitialized,
      authError,
      ...callbacks,
    }),
    [
      accessToken,
      customer,
      customerStatus,
      isAuthLoading,
      isInitialized,
      authError,
      callbacks,
    ]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
